import { FiatPrice, RewardsAccount, RewardsPrice } from "redmond";
import { Prices } from "@b2bportal/air-booking-api";
import { roundToTwoDecimals } from "halifax";

export interface IRewardsAllocation {
  accountReferenceId: string;
  fiat: FiatPrice;
  rewards: { currency: string; value: number };
}

export interface ISelectedRewardsAccountInput {
  accountReferenceId: string;
  customFiatValue?: number;
}

const resolveZeroedAllocation = (
  accountReferenceId: string,
  account: RewardsAccount | undefined,
  currency: { currencyCode: string; currencySymbol: string }
): IRewardsAllocation => ({
  accountReferenceId,
  fiat: { ...currency, value: 0 },
  rewards: { currency: account?.rewardsBalance.currency ?? "", value: 0 },
});

const resolveAccountAllocation = ({
  accountReferenceId,
  customFiatValue,
  account,
  accountSpecificRewardsRate,
  remainingBalance,
  bookingTotalFiat,
  currency,
}: {
  accountReferenceId: string;
  customFiatValue: number | undefined;
  account: RewardsAccount;
  accountSpecificRewardsRate: { currency: string; value: number };
  remainingBalance: number;
  bookingTotalFiat: number;
  currency: { currencyCode: string; currencySymbol: string };
}): { allocation: IRewardsAllocation; fiatApplied: number } => {
  const desiredFiat = customFiatValue ?? account.rewardsCashEquivalent.value;
  const fiatApplied = Math.min(remainingBalance, desiredFiat);

  const rewardsNeededForFullBookingTotal = accountSpecificRewardsRate.value;
  const fractionOfBookingTotalApplied =
    bookingTotalFiat > 0 ? fiatApplied / bookingTotalFiat : 0;
  const rewardsApplied =
    rewardsNeededForFullBookingTotal * fractionOfBookingTotalApplied;

  return {
    allocation: {
      accountReferenceId,
      fiat: { ...currency, value: roundToTwoDecimals(fiatApplied) },
      rewards: {
        currency: accountSpecificRewardsRate.currency,
        value: roundToTwoDecimals(rewardsApplied),
      },
    },
    fiatApplied,
  };
};

export const resolveSplitPayRowAmounts = (
  selectedEntry:
    | {
        rewardsPaymentTotal?: { value: number; currency: string } | null;
        rewardsPaymentInFiatCurrency?: FiatPrice | null;
      }
    | undefined,
  allocation: IRewardsAllocation | undefined
): { customAmount: number | undefined; fiat: FiatPrice | null } => ({
  customAmount:
    selectedEntry?.rewardsPaymentTotal?.value ?? allocation?.rewards.value,
  fiat: selectedEntry?.rewardsPaymentInFiatCurrency ?? allocation?.fiat ?? null,
});

export const resolveRewardsAllocations = (
  selectedAccounts: ISelectedRewardsAccountInput[],
  rewardsAccounts: RewardsAccount[],
  total: Prices | undefined,
  currency: { currencyCode: string; currencySymbol: string }
): IRewardsAllocation[] => {
  const bookingTotalFiat = total?.fiat.value ?? 0;
  let remainingBalance = bookingTotalFiat;

  return selectedAccounts.map(({ accountReferenceId, customFiatValue }) => {
    const account = rewardsAccounts.find(
      (rewardsAccount) =>
        rewardsAccount.accountReferenceId === accountReferenceId
    );
    const accountSpecificRewardsRate =
      total?.accountSpecific?.[accountReferenceId];

    if (remainingBalance <= 0 || !account || !accountSpecificRewardsRate) {
      return resolveZeroedAllocation(accountReferenceId, account, currency);
    }

    const { allocation, fiatApplied } = resolveAccountAllocation({
      accountReferenceId,
      customFiatValue,
      account,
      accountSpecificRewardsRate,
      remainingBalance,
      bookingTotalFiat,
      currency,
    });
    remainingBalance -= fiatApplied;

    return allocation;
  });
};

// Caps a custom rewards amount at what this account alone would need to cover the
// entire booking total - the same validation already applied to the single-account
// rewards flow (SET_SELECTED_REWARDS_PAYMENT_TOTAL).
export const resolveCappedRewardsAccountAmount = (
  rewardsPaymentTotal: RewardsPrice | null,
  rewardsPaymentInFiatCurrency: FiatPrice | null,
  accountReferenceId: string,
  total?: Prices
): {
  rewardsPaymentTotal: RewardsPrice | null;
  rewardsPaymentInFiatCurrency: FiatPrice | null;
} => {
  if (!rewardsPaymentTotal || !rewardsPaymentInFiatCurrency) {
    return { rewardsPaymentTotal: null, rewardsPaymentInFiatCurrency: null };
  }

  // This occurs when fetching rewards fails during search, don't allow the user to accidentally use all their rewards
  if (total && !total.accountSpecific[accountReferenceId]) {
    return { rewardsPaymentTotal: null, rewardsPaymentInFiatCurrency: null };
  }

  if (
    total &&
    total.accountSpecific[accountReferenceId].value < rewardsPaymentTotal.value
  ) {
    return {
      rewardsPaymentInFiatCurrency: total.fiat,
      rewardsPaymentTotal: total.accountSpecific[accountReferenceId],
    };
  }

  return { rewardsPaymentTotal, rewardsPaymentInFiatCurrency };
};

// Caps a custom rewards amount for one account in a multi-rewards checkout at what's
// still needed once the other selected accounts' allocations are taken into account,
// by re-running the real allocation math (remaining balance, in selection order) with
// this account's candidate amount substituted in.
export const resolveCappedCustomAccountAllocation = (
  selectedAccounts: {
    accountReferenceId: string;
    rewardsPaymentInFiatCurrency: FiatPrice | null;
  }[],
  rewardsAccounts: RewardsAccount[],
  total: Prices | undefined,
  accountReferenceId: string,
  candidateFiatValue: number
): IRewardsAllocation | undefined => {
  if (!total) {
    return undefined;
  }

  const candidateAccounts: ISelectedRewardsAccountInput[] =
    selectedAccounts.map((selectedAccount) =>
      selectedAccount.accountReferenceId === accountReferenceId
        ? { accountReferenceId, customFiatValue: candidateFiatValue }
        : {
            accountReferenceId: selectedAccount.accountReferenceId,
            customFiatValue:
              selectedAccount.rewardsPaymentInFiatCurrency?.value ??
              undefined,
          }
    );

  return resolveRewardsAllocations(candidateAccounts, rewardsAccounts, total, {
    currencyCode: total.fiat.currencyCode,
    currencySymbol: total.fiat.currencySymbol,
  }).find(
    (allocation) => allocation.accountReferenceId === accountReferenceId
  );
};
